class MHeader extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      headers: ['header'],
      logos: ['.m-logo'],
      topbar: '.m-topbar',
      headerWrapper: '.m-header__wrapper',
      topbarClose: '.m-topbar__close',
    };

    this.classes = {
      scrollUp: 'scroll-up',
      scrollDown: 'scroll-down',
      stuck: 'stuck',
      always: 'header-sticky-always',
      headerScrollUp: 'header-scroll-up',
      headerScrollDown: 'header-scroll-down',
    };

    this.domNodes = queryDomNodes(this.selectors, this);

    this.stickyHeader = this.dataset.sticky || 'none';

    this.headerHeight = 0;
    this.headerOffsetTop = 0;
    this.reservedHeaderHeight = 0;
    this.stickyTriggerPoint = 0;
    this.lastScroll = window.scrollY || 0;

    this.scrollHandler = null;
    this.resizeHandler = null;
    this.loadHandler = null;
    this.breakpointHandler = null;
    this.scrollAnimationFrame = null;
    this.resizeAnimationFrame = null;
    this.measurementTimers = [];
    this.resizeObserver = null;
    this.isMeasuring = false;

    this.megamenu = null;
    this.siteNav = null;

    this.initialized = false;

    this.init();
  }

  init() {
    if (this.initialized) return;

    this.initialized = true;

    this.transparentHeader =
      this.domNodes?.headers?.[0]?.dataset.transparent === 'true';

    this.updateHeaderMeasurements(true);
    this.initializeStickyBehavior();
    this.initializeResponsiveBehavior();
    this.initializeMeasurementObservers();
    this.initializeOptionalFeatures();
  }

  initializeStickyBehavior() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    this.scrollHandler = () => {
      if (this.scrollAnimationFrame !== null) return;

      this.scrollAnimationFrame = window.requestAnimationFrame(() => {
        this.scrollAnimationFrame = null;
        this.applyStickyState(window.scrollY || 0);
      });
    };

    window.addEventListener('scroll', this.scrollHandler, {
      passive: true,
    });

    this.applyStickyState(window.scrollY || 0);
  }

  initializeResponsiveBehavior() {
    this.breakpointHandler = () => {
      this.refreshStickyMeasurements(true);
    };

    document.addEventListener(
      'matchMobile',
      this.breakpointHandler
    );

    document.addEventListener(
      'unmatchMobile',
      this.breakpointHandler
    );

    this.resizeHandler = () => {
      if (this.resizeAnimationFrame !== null) {
        window.cancelAnimationFrame(
          this.resizeAnimationFrame
        );
      }

      this.resizeAnimationFrame =
        window.requestAnimationFrame(() => {
          this.resizeAnimationFrame = null;
          this.refreshStickyMeasurements(true);
        });
    };

    window.addEventListener(
      'resize',
      this.resizeHandler,
      {
        passive: true,
      }
    );

    this.loadHandler = () => {
      this.refreshStickyMeasurements(true);
    };

    window.addEventListener(
      'load',
      this.loadHandler,
      {
        once: true,
      }
    );
  }

  initializeMeasurementObservers() {
    const headerWrapper = this.domNodes.headerWrapper;

    if (
      headerWrapper &&
      typeof ResizeObserver === 'function'
    ) {
      this.resizeObserver = new ResizeObserver(() => {
        const isSticky =
          this.classList.contains(this.classes.stuck) ||
          this.classList.contains(this.classes.always);

        if (isSticky || this.isMeasuring) return;

        this.scheduleMeasurement();
      });

      this.resizeObserver.observe(headerWrapper);
    }

    if (document.fonts?.ready) {
      document.fonts.ready
        .then(() => {
          if (!this.isConnected) return;

          this.refreshStickyMeasurements(true);
        })
        .catch(() => {});
    }

    this.measurementTimers.push(
      window.setTimeout(() => {
        if (!this.isConnected) return;

        this.refreshStickyMeasurements(true);
      }, 300)
    );

    this.measurementTimers.push(
      window.setTimeout(() => {
        if (!this.isConnected) return;

        this.refreshStickyMeasurements(true);
      }, 900)
    );
  }

  scheduleMeasurement() {
    if (this.resizeAnimationFrame !== null) return;

    this.resizeAnimationFrame =
      window.requestAnimationFrame(() => {
        this.resizeAnimationFrame = null;
        this.refreshStickyMeasurements();
      });
  }

  refreshStickyMeasurements(force = false) {
    this.updateHeaderMeasurements(force);
    this.applyStickyState(window.scrollY || 0);
  }

  initializeOptionalFeatures() {
    if (typeof Megamenu === 'function') {
      try {
        this.megamenu = new Megamenu(this);
      } catch (error) {
        console.warn(
          '[Header] Megamenu initialization was skipped:',
          error
        );
      }
    }

    if (typeof SiteNav === 'function') {
      try {
        this.siteNav = new SiteNav(this);
      } catch (error) {
        console.warn(
          '[Header] Site navigation initialization was skipped:',
          error
        );
      }
    }

    window.__sfHeader = this;

    if (
      window.Shopify?.designMode &&
      window.MinimogTheme?.Wishlist &&
      typeof window.MinimogTheme.Wishlist
        .updateWishlistCount === 'function'
    ) {
      try {
        window.MinimogTheme.Wishlist
          .updateWishlistCount();
      } catch (error) {
        console.warn(
          '[Header] Wishlist count could not be updated:',
          error
        );
      }
    }
  }

  getActiveHeader() {
    const mobileHeader = this.querySelector(
      'header.m-header__mobile'
    );

    const desktopHeader = this.querySelector(
      'header.m-header__desktop'
    );

    return window.innerWidth < 1024
      ? mobileHeader
      : desktopHeader;
  }

  getTopbarHeight() {
    const topbar = this.querySelector(
      this.selectors.topbar
    );

    return topbar?.offsetHeight || 0;
  }

  captureStickyState() {
    return {
      stuck: this.classList.contains(
        this.classes.stuck
      ),

      always: this.classList.contains(
        this.classes.always
      ),

      scrollUp: this.classList.contains(
        this.classes.scrollUp
      ),

      scrollDown: this.classList.contains(
        this.classes.scrollDown
      ),

      bodyScrollUp: document.body.classList.contains(
        this.classes.headerScrollUp
      ),

      bodyScrollDown:
        document.body.classList.contains(
          this.classes.headerScrollDown
        ),
    };
  }

  clearMeasurementState() {
    this.classList.remove(
      this.classes.stuck,
      this.classes.always,
      this.classes.scrollUp,
      this.classes.scrollDown
    );

    document.body.classList.remove(
      this.classes.headerScrollUp,
      this.classes.headerScrollDown
    );
  }

  restoreStickyState(state) {
    if (state.stuck) {
      this.classList.add(this.classes.stuck);
    }

    if (state.always) {
      this.classList.add(this.classes.always);
    }

    if (state.scrollUp) {
      this.classList.add(this.classes.scrollUp);
    }

    if (state.scrollDown) {
      this.classList.add(
        this.classes.scrollDown
      );
    }

    if (state.bodyScrollUp) {
      document.body.classList.add(
        this.classes.headerScrollUp
      );
    }

    if (state.bodyScrollDown) {
      document.body.classList.add(
        this.classes.headerScrollDown
      );
    }
  }

  updateHeaderMeasurements(force = false) {
  if (this.isMeasuring) return;

  const headerWrapper = this.domNodes.headerWrapper;
  const activeHeader = this.getActiveHeader();

  if (!headerWrapper || !activeHeader) return;

  this.isMeasuring = true;

  const previousState = this.captureStickyState();

  const mobileRevealShell =
    window.innerWidth < 1024
      ? activeHeader.querySelector(
          '.ca-mobile-category-shell--homepage-reveal'
        )
      : null;

  const previousRevealDisplay =
    mobileRevealShell?.style.getPropertyValue('display') || '';

  const previousRevealPriority =
    mobileRevealShell?.style.getPropertyPriority('display') || '';

  try {
    this.clearMeasurementState();

    if (mobileRevealShell) {
      mobileRevealShell.style.setProperty(
        'display',
        'none',
        'important'
      );
    }

    const wrapperRect =
      headerWrapper.getBoundingClientRect();

    const wrapperHeight =
      headerWrapper.offsetHeight;

    const activeHeaderHeight =
      activeHeader.offsetHeight;

    const topbarHeight =
      this.getTopbarHeight();

    const wrapperDocumentTop =
      wrapperRect.top + (window.scrollY || 0);

    const expandedHeaderHeight =
      topbarHeight + wrapperHeight;

    this.headerHeight = wrapperHeight;
    this.headerOffsetTop = wrapperDocumentTop;

    this.stickyTriggerPoint =
      wrapperDocumentTop + wrapperHeight;

    if (
      force ||
      expandedHeaderHeight > this.reservedHeaderHeight
    ) {
      this.reservedHeaderHeight =
        expandedHeaderHeight;
    }

    if (this.reservedHeaderHeight > 0) {
      this.style.minHeight =
        `${this.reservedHeaderHeight}px`;

      document.documentElement.style.setProperty(
        '--m-header-reserved-height',
        `${this.reservedHeaderHeight}px`
      );
    }

    document.documentElement.style.setProperty(
      '--m-header-height',
      `${activeHeaderHeight}px`
    );

    document.documentElement.style.setProperty(
      '--m-header-wrapper-height',
      `${wrapperHeight}px`
    );

    if (window.MinimogSettings) {
      window.MinimogSettings.headerHeight =
        activeHeaderHeight;
    }
  } finally {
    if (mobileRevealShell) {
      if (previousRevealDisplay) {
        mobileRevealShell.style.setProperty(
          'display',
          previousRevealDisplay,
          previousRevealPriority
        );
      } else {
        mobileRevealShell.style.removeProperty('display');
      }
    }

    this.restoreStickyState(previousState);
    this.isMeasuring = false;
  }
}

  clearStickyState() {
    this.classList.remove(
      this.classes.scrollUp,
      this.classes.scrollDown,
      this.classes.stuck,
      this.classes.always
    );

    document.body.classList.remove(
      this.classes.headerScrollUp,
      this.classes.headerScrollDown
    );
  }

  applyStickyState(currentScroll) {
    this.stickyHeader =
      this.dataset.sticky || 'none';

    const triggerPoint =
      Number.isFinite(this.stickyTriggerPoint)
        ? this.stickyTriggerPoint
        : 0;

    if (
      this.stickyHeader === 'none' ||
      currentScroll <= triggerPoint
    ) {
      this.clearStickyState();
      this.lastScroll = currentScroll;
      return;
    }

    if (this.stickyHeader === 'on_scroll_up') {
      this.classList.add(
        this.classes.stuck
      );

      this.classList.remove(
        this.classes.always
      );
    } else if (
      this.stickyHeader === 'always'
    ) {
      this.classList.add(
        this.classes.always
      );

      this.classList.remove(
        this.classes.stuck
      );
    } else {
      this.clearStickyState();
      this.lastScroll = currentScroll;
      return;
    }

    this.classList.remove(
      this.classes.scrollDown
    );

    this.classList.add(
      this.classes.scrollUp
    );

    document.body.classList.remove(
      this.classes.headerScrollDown
    );

    document.body.classList.add(
      this.classes.headerScrollUp
    );

    this.lastScroll = currentScroll;
  }

  disconnectedCallback() {
    if (this.scrollHandler) {
      window.removeEventListener(
        'scroll',
        this.scrollHandler
      );

      this.scrollHandler = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener(
        'resize',
        this.resizeHandler
      );

      this.resizeHandler = null;
    }

    if (this.loadHandler) {
      window.removeEventListener(
        'load',
        this.loadHandler
      );

      this.loadHandler = null;
    }

    if (this.breakpointHandler) {
      document.removeEventListener(
        'matchMobile',
        this.breakpointHandler
      );

      document.removeEventListener(
        'unmatchMobile',
        this.breakpointHandler
      );

      this.breakpointHandler = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.scrollAnimationFrame !== null) {
      window.cancelAnimationFrame(
        this.scrollAnimationFrame
      );

      this.scrollAnimationFrame = null;
    }

    if (this.resizeAnimationFrame !== null) {
      window.cancelAnimationFrame(
        this.resizeAnimationFrame
      );

      this.resizeAnimationFrame = null;
    }

    this.measurementTimers.forEach((timer) => {
      window.clearTimeout(timer);
    });

    this.measurementTimers = [];

    if (window.__sfHeader === this) {
      window.__sfHeader = null;
    }
  }
}

if (!customElements.get('m-header')) {
  customElements.define('m-header', MHeader);
}